import type { CSSProperties } from 'react';
import { Toast } from '@zep-us/zus/toast';
import { useTranslation } from 'next-i18next';
import { JellyIcon } from '@/icons';
import { cn } from '@/utils/tailwind.util';

/** 라이브 수신 토스트와 같은 순차 반짝임 배치. */
const SPARKLES = [{
  className: 'left-[8px] top-[10px]',
  size: 7,
  x: -7,
  y: -9,
  delay: 640,
  duration: 760
}, {
  className: 'left-[34px] top-[3px]',
  size: 10,
  x: 5,
  y: -11,
  delay: 700,
  duration: 900
}, {
  className: 'left-[6px] top-[42px]',
  size: 6,
  x: -9,
  y: 7,
  delay: 830,
  duration: 820
}, {
  className: '-left-[7px] -top-[8px]',
  size: 9,
  x: -10,
  y: -8,
  delay: 770,
  duration: 950
}, {
  className: 'right-[44px] -top-[7px]',
  size: 12,
  x: 4,
  y: -12,
  delay: 900,
  duration: 1020
}, {
  className: '-right-[6px] top-[12px]',
  size: 8,
  x: 11,
  y: -5,
  delay: 1010,
  duration: 880
}, {
  className: 'right-[16px] -bottom-[7px]',
  size: 7,
  x: 6,
  y: 9,
  delay: 1140,
  duration: 840
}] as const;
const SparkleGlyph = ({
  size,
  className,
  style
}: {
  size: number;
  className: string;
  style: CSSProperties;
}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className={cn('drop-shadow-[0_0_3px_rgba(255,255,255,0.95)]', className)} style={style} data-sentry-element="svg" data-sentry-component="SparkleGlyph" data-sentry-source-file="PointReceiveToast.tsx">
    <path d="M12 1c.6 6.2 4.8 10.4 11 11-6.2.6-10.4 4.8-11 11-.6-6.2-4.8-10.4-11-11 6.2-.6 10.4-4.8 11-11Z" fill="#FFFFFF" data-sentry-element="path" data-sentry-source-file="PointReceiveToast.tsx" />
  </svg>;

/**
 * 학생이 젤리를 받았을 때 라이브와 동일한 반짝임 피드백을 노출한다.
 *
 * 껍데기는 디자인 시스템의 `Toast` 다 — 표면·타이포·간격을 직접 그리면 구매
 * 토스트(`PurchaseToast`)와 같은 자리에서 미세하게 어긋난다.
 *
 * 연출(입·퇴장, sweep, 반짝임)은 이 토스트만의 축하라 `Toast` 밖에서 얹는다.
 */
export function PointReceiveToast({
  amount,
  reason,
  isVisible = true
}: {
  amount: number;
  reason?: string;
  isVisible?: boolean;
}) {
  const {
    t
  } = useTranslation('common');
  return (
    // 반짝임은 토스트 바깥으로 삐져나가는 게 연출이라, 자르지 않는 래퍼가 필요하다.
    <div className="relative isolate w-fit" data-sentry-component="PointReceiveToast" data-sentry-source-file="PointReceiveToast.tsx">
      {SPARKLES.map(sparkle => <SparkleGlyph key={sparkle.className} size={sparkle.size}
      // sparkle.className 은 좌표다. 여기서 빠뜨리면 일곱 개가 전부 같은
      // 자리에 겹쳐 떠서, 토스트 주변에 흩뿌려지는 반짝임이 사라진다.
      className={cn('pointer-events-none absolute z-10 animate-point-sparkle-twinkle motion-reduce:hidden', sparkle.className)} style={{
        animationDelay: `${sparkle.delay}ms`,
        animationDuration: `${sparkle.duration}ms`,
        '--sparkle-x': `${sparkle.x}px`,
        '--sparkle-y': `${sparkle.y}px`
      } as CSSProperties} />)}
      <Toast title={t('pointReceiveToast.message', {
        amount
      })} description={reason} leftSlot={<JellyIcon size="md" className="shrink-0 animate-point-coin-celebrate motion-reduce:animate-none" />} className={cn('max-w-[320px]', isVisible ? 'animate-point-toast-in' : 'animate-point-toast-out', 'motion-reduce:animate-none')} data-sentry-element="Toast" data-sentry-source-file="PointReceiveToast.tsx" />
      {/**
       * sweep 은 표면 위를 지나는 빛이라 `Toast` 위에 덮는다. `Toast` 는 내부
       * JSX 가 children 을 덮어써서 안으로는 못 넣는다 — 래퍼가 토스트와 같은
       * 크기(w-fit)라 inset-0 이 곧 토스트 표면이다.
       */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-sm">
        <span className="absolute inset-0 animate-point-toast-sweep bg-[linear-gradient(105deg,transparent_34%,rgba(255,255,255,0.45)_44%,rgba(255,255,255,0.95)_50%,rgba(255,255,255,0.45)_56%,transparent_66%)] bg-[length:200%_100%] bg-no-repeat motion-reduce:hidden" />
      </span>
    </div>
  );
}